import { type AnalyticsBrowser } from '@segment/analytics-next'

export const SEGMENT_EVENTS = {
  // Campaign Events
  CAMPAIGN_CREATED: 'campaign.created',
  CAMPAIGN_SENT: 'campaign.sent',
  CAMPAIGN_SCHEDULED: 'campaign.scheduled',
  CAMPAIGN_EDITED: 'campaign.edited',
  CAMPAIGN_DELETED: 'campaign.deleted',
  CAMPAIGN_PAUSED: 'campaign.paused',
  CAMPAIGN_RESUMED: 'campaign.resumed',

  // Customer Events
  CUSTOMER_CREATED: 'customer.created',
  CUSTOMER_EDITED: 'customer.edited',
  CUSTOMER_DELETED: 'customer.deleted',
  CUSTOMER_IMPORTED: 'customer.imported',
  CUSTOMER_EXPORTED: 'customer.exported',
  CUSTOMER_MERGED: 'customer.merged',
  CUSTOMER_TAGGED: 'customer.tagged',
  CUSTOMER_NOTE_ADDED: 'customer.note_added',

  // CSV Import Events
  CSV_UPLOADED: 'csv.uploaded',
  CSV_IMPORT_STARTED: 'csv.import_started',
  CSV_IMPORT_COMPLETED: 'csv.import_completed',
  CSV_IMPORT_FAILED: 'csv.import_failed',

  // Settings Events
  SETTINGS_UPDATED: 'settings.updated',
  PROFILE_UPDATED: 'profile.updated',
  PASSWORD_CHANGED: 'password.changed',
  NOTIFICATION_PREFERENCES_UPDATED: 'notification_preferences.updated',
  INTEGRATION_CONNECTED: 'integration.connected',
  INTEGRATION_DISCONNECTED: 'integration.disconnected',

  // Booking Events
  BOOKING_CREATED: 'booking.created',
  BOOKING_EDITED: 'booking.edited',
  BOOKING_CANCELLED: 'booking.cancelled',
  BOOKING_CONFIRMED: 'booking.confirmed',
  BOOKING_COMPLETED: 'booking.completed',
  BOOKING_NO_SHOW: 'booking.no_show',

  // WiFi Events
  WIFI_PORTAL_VIEWED: 'wifi.portal_viewed',
  WIFI_TERMS_ACCEPTED: 'wifi.terms_accepted',
  WIFI_LOGIN_COMPLETED: 'wifi.login_completed',
  WIFI_PORTAL_CUSTOMISED: 'wifi.portal_customised',
  WIFI_SETTINGS_UPDATED: 'wifi.settings_updated',

  // Review Events
  REVIEW_RECEIVED: 'review.received',
  REVIEW_RESPONDED: 'review.responded',
  REVIEW_FLAGGED: 'review.flagged',
  REVIEW_REQUEST_SENT: 'review.request_sent',
  REVIEW_AUTOMATION_CREATED: 'review.automation_created',

  // Loyalty Events
  LOYALTY_CARD_CREATED: 'loyalty.card_created',
  LOYALTY_CARD_ACTIVATED: 'loyalty.card_activated',
  LOYALTY_POINTS_EARNED: 'loyalty.points_earned',
  LOYALTY_POINTS_REDEEMED: 'loyalty.points_redeemed',
  LOYALTY_REWARD_CLAIMED: 'loyalty.reward_claimed',
  LOYALTY_TIER_CHANGED: 'loyalty.tier_changed',

  // Analytics Events
  REPORT_VIEWED: 'report.viewed',
  REPORT_EXPORTED: 'report.exported',
  DASHBOARD_VIEWED: 'dashboard.viewed',
  METRIC_FILTERED: 'metric.filtered',
  DATE_RANGE_CHANGED: 'date_range.changed',

  // Workflow Events
  WORKFLOW_CREATED: 'workflow.created',
  WORKFLOW_EDITED: 'workflow.edited',
  WORKFLOW_DELETED: 'workflow.deleted',
  WORKFLOW_ACTIVATED: 'workflow.activated',
  WORKFLOW_DEACTIVATED: 'workflow.deactivated',
  WORKFLOW_TESTED: 'workflow.tested',

  // User Management Events
  USER_INVITED: 'user.invited',
  USER_REMOVED: 'user.removed',
  USER_ROLE_CHANGED: 'user.role_changed',
  TEAM_CREATED: 'team.created',
  TEAM_UPDATED: 'team.updated',

  // Feature Usage Events
  FEATURE_ACCESSED: 'feature.accessed',
  FEATURE_CONFIGURED: 'feature.configured',
  SEARCH_PERFORMED: 'search.performed',
  FILTER_APPLIED: 'filter.applied',
  EXPORT_INITIATED: 'export.initiated',

  // What's New Events
  WHATS_NEW_POPUP_VIEWED: 'whats_new.popup_viewed',
  WHATS_NEW_POPUP_DISMISSED: 'whats_new.popup_dismissed',
  WHATS_NEW_FEATURE_CLICKED: 'whats_new.feature_clicked',
  WHATS_NEW_PAGE_VIEWED: 'whats_new.page_viewed',
  WHATS_NEW_SALES_CLICKED: 'whats_new.sales_clicked',
  WHATS_NEW_SEE_ALL_CLICKED: 'whats_new.see_all_clicked',

  // Error Events
  ERROR_OCCURRED: 'error.occurred',
  API_ERROR: 'api.error',
  VALIDATION_ERROR: 'validation.error',
} as const

export type SegmentEventName =
  (typeof SEGMENT_EVENTS)[keyof typeof SEGMENT_EVENTS]

interface BaseEventProperties {
  organisation_id?: string
  venue_id?: string
  user_id?: string
  timestamp?: string
}

interface CampaignEventProperties extends BaseEventProperties {
  campaign_id: string
  campaign_type?: 'email' | 'sms' | 'push'
  campaign_name?: string
  recipient_count?: number
}

interface CustomerEventProperties extends BaseEventProperties {
  customer_id: string
  customer_email?: string
  source?: string
}

interface CSVEventProperties extends BaseEventProperties {
  file_name: string
  file_size: number
  row_count?: number
  success_count?: number
  error_count?: number
  import_type?: string
}

interface SettingsEventProperties extends BaseEventProperties {
  setting_type: string
  setting_name: string
  old_value?: any
  new_value?: any
}

interface BookingEventProperties extends BaseEventProperties {
  booking_id: string
  booking_date?: string
  party_size?: number
  booking_type?: string
}

interface WiFiEventProperties extends BaseEventProperties {
  portal_id?: string
  login_method?: string
  device_type?: string
}

interface ReviewEventProperties extends BaseEventProperties {
  review_id?: string
  platform?: string
  rating?: number
}

interface LoyaltyEventProperties extends BaseEventProperties {
  card_id?: string
  customer_id?: string
  points?: number
  tier?: string
}

interface AnalyticsEventProperties extends BaseEventProperties {
  report_type?: string
  date_range?: string
  filters?: Record<string, any>
}

interface WorkflowEventProperties extends BaseEventProperties {
  workflow_id: string
  workflow_name?: string
  workflow_type?: string
  trigger_type?: string
}

interface FeatureEventProperties extends BaseEventProperties {
  feature_name: string
  action?: string
  metadata?: Record<string, any>
}

interface WhatsNewEventProperties extends BaseEventProperties {
  announcement_id: string
  source?: 'popup' | 'page'
  feature_id?: string
  feature_name?: string
  preview?: boolean
}

interface ErrorEventProperties extends BaseEventProperties {
  error_message: string
  error_code?: string
  error_type?: string
  stack_trace?: string
  url?: string
}

export type EventProperties =
  | CampaignEventProperties
  | CustomerEventProperties
  | CSVEventProperties
  | SettingsEventProperties
  | BookingEventProperties
  | WiFiEventProperties
  | ReviewEventProperties
  | LoyaltyEventProperties
  | AnalyticsEventProperties
  | WorkflowEventProperties
  | FeatureEventProperties
  | WhatsNewEventProperties
  | ErrorEventProperties
  | BaseEventProperties

export class SegmentTracker {
  private analytics: AnalyticsBrowser | null

  constructor(analytics: AnalyticsBrowser | null) {
    this.analytics = analytics
  }

  private getCommonProperties(): BaseEventProperties {
    return {
      timestamp: new Date().toISOString(),
    }
  }

  track(eventName: SegmentEventName, properties?: EventProperties) {
    const eventProperties = {
      ...this.getCommonProperties(),
      ...properties,
    }

    if (this.analytics) {
      this.analytics.track(eventName, eventProperties)
    } else if (import.meta.env?.DEV) {
      console.log('[Segment Track]', eventName, eventProperties)
    }
  }

  // Campaign tracking methods
  trackCampaignCreated(properties: CampaignEventProperties) {
    this.track(SEGMENT_EVENTS.CAMPAIGN_CREATED, properties)
  }

  trackCampaignSent(properties: CampaignEventProperties) {
    this.track(SEGMENT_EVENTS.CAMPAIGN_SENT, properties)
  }

  trackCampaignScheduled(properties: CampaignEventProperties) {
    this.track(SEGMENT_EVENTS.CAMPAIGN_SCHEDULED, properties)
  }

  // Customer tracking methods
  trackCustomerCreated(properties: CustomerEventProperties) {
    this.track(SEGMENT_EVENTS.CUSTOMER_CREATED, properties)
  }

  trackCustomerImported(
    properties: CustomerEventProperties & { import_count: number }
  ) {
    this.track(SEGMENT_EVENTS.CUSTOMER_IMPORTED, properties)
  }

  // CSV tracking methods
  trackCSVUploaded(properties: CSVEventProperties) {
    this.track(SEGMENT_EVENTS.CSV_UPLOADED, properties)
  }

  trackCSVImportCompleted(properties: CSVEventProperties) {
    this.track(SEGMENT_EVENTS.CSV_IMPORT_COMPLETED, properties)
  }

  // Settings tracking methods
  trackSettingsUpdated(properties: SettingsEventProperties) {
    this.track(SEGMENT_EVENTS.SETTINGS_UPDATED, properties)
  }

  // Booking tracking methods
  trackBookingCreated(properties: BookingEventProperties) {
    this.track(SEGMENT_EVENTS.BOOKING_CREATED, properties)
  }

  // Feature usage tracking
  trackFeatureAccessed(featureName: string, metadata?: Record<string, any>) {
    this.track(SEGMENT_EVENTS.FEATURE_ACCESSED, {
      feature_name: featureName,
      metadata,
    })
  }

  // Error tracking
  trackError(
    error: Error,
    additionalProperties?: Partial<ErrorEventProperties>
  ) {
    this.track(SEGMENT_EVENTS.ERROR_OCCURRED, {
      error_message: error.message,
      error_type: error.name,
      stack_trace: error.stack,
      ...additionalProperties,
    })
  }

  // Additional Campaign tracking methods
  trackCampaignEdited(properties: CampaignEventProperties) {
    this.track(SEGMENT_EVENTS.CAMPAIGN_EDITED, properties)
  }

  trackCampaignDeleted(properties: CampaignEventProperties) {
    this.track(SEGMENT_EVENTS.CAMPAIGN_DELETED, properties)
  }

  trackCampaignPaused(properties: CampaignEventProperties) {
    this.track(SEGMENT_EVENTS.CAMPAIGN_PAUSED, properties)
  }

  trackCampaignResumed(properties: CampaignEventProperties) {
    this.track(SEGMENT_EVENTS.CAMPAIGN_RESUMED, properties)
  }

  // Additional Customer tracking methods
  trackCustomerEdited(properties: CustomerEventProperties) {
    this.track(SEGMENT_EVENTS.CUSTOMER_EDITED, properties)
  }

  trackCustomerDeleted(properties: CustomerEventProperties) {
    this.track(SEGMENT_EVENTS.CUSTOMER_DELETED, properties)
  }

  trackCustomerExported(
    properties: CustomerEventProperties & { export_count: number }
  ) {
    this.track(SEGMENT_EVENTS.CUSTOMER_EXPORTED, properties)
  }

  trackCustomerMerged(
    properties: CustomerEventProperties & { merged_into_id: string }
  ) {
    this.track(SEGMENT_EVENTS.CUSTOMER_MERGED, properties)
  }

  trackCustomerTagged(
    properties: CustomerEventProperties & { tags: string[] }
  ) {
    this.track(SEGMENT_EVENTS.CUSTOMER_TAGGED, properties)
  }

  trackCustomerNoteAdded(
    properties: CustomerEventProperties & { note_content?: string }
  ) {
    this.track(SEGMENT_EVENTS.CUSTOMER_NOTE_ADDED, properties)
  }

  // Additional CSV tracking methods
  trackCSVImportStarted(properties: CSVEventProperties) {
    this.track(SEGMENT_EVENTS.CSV_IMPORT_STARTED, properties)
  }

  trackCSVImportFailed(
    properties: CSVEventProperties & { error_message: string }
  ) {
    this.track(SEGMENT_EVENTS.CSV_IMPORT_FAILED, properties)
  }

  // Additional Settings tracking methods
  trackProfileUpdated(properties: SettingsEventProperties) {
    this.track(SEGMENT_EVENTS.PROFILE_UPDATED, properties)
  }

  trackPasswordChanged(
    properties: Omit<SettingsEventProperties, 'old_value' | 'new_value'>
  ) {
    this.track(SEGMENT_EVENTS.PASSWORD_CHANGED, properties)
  }

  trackNotificationPreferencesUpdated(properties: SettingsEventProperties) {
    this.track(SEGMENT_EVENTS.NOTIFICATION_PREFERENCES_UPDATED, properties)
  }

  trackIntegrationConnected(
    properties: SettingsEventProperties & { integration_type: string }
  ) {
    this.track(SEGMENT_EVENTS.INTEGRATION_CONNECTED, properties)
  }

  trackIntegrationDisconnected(
    properties: SettingsEventProperties & { integration_type: string }
  ) {
    this.track(SEGMENT_EVENTS.INTEGRATION_DISCONNECTED, properties)
  }

  // Additional Booking tracking methods
  trackBookingEdited(properties: BookingEventProperties) {
    this.track(SEGMENT_EVENTS.BOOKING_EDITED, properties)
  }

  trackBookingCancelled(properties: BookingEventProperties) {
    this.track(SEGMENT_EVENTS.BOOKING_CANCELLED, properties)
  }

  trackBookingConfirmed(properties: BookingEventProperties) {
    this.track(SEGMENT_EVENTS.BOOKING_CONFIRMED, properties)
  }

  trackBookingCompleted(properties: BookingEventProperties) {
    this.track(SEGMENT_EVENTS.BOOKING_COMPLETED, properties)
  }

  trackBookingNoShow(properties: BookingEventProperties) {
    this.track(SEGMENT_EVENTS.BOOKING_NO_SHOW, properties)
  }

  // WiFi tracking methods
  trackWiFiPortalViewed(properties: WiFiEventProperties) {
    this.track(SEGMENT_EVENTS.WIFI_PORTAL_VIEWED, properties)
  }

  trackWiFiTermsAccepted(properties: WiFiEventProperties) {
    this.track(SEGMENT_EVENTS.WIFI_TERMS_ACCEPTED, properties)
  }

  trackWiFiLoginCompleted(properties: WiFiEventProperties) {
    this.track(SEGMENT_EVENTS.WIFI_LOGIN_COMPLETED, properties)
  }

  trackWiFiPortalCustomised(properties: WiFiEventProperties) {
    this.track(SEGMENT_EVENTS.WIFI_PORTAL_CUSTOMISED, properties)
  }

  trackWiFiSettingsUpdated(properties: WiFiEventProperties) {
    this.track(SEGMENT_EVENTS.WIFI_SETTINGS_UPDATED, properties)
  }

  // Review tracking methods
  trackReviewReceived(properties: ReviewEventProperties) {
    this.track(SEGMENT_EVENTS.REVIEW_RECEIVED, properties)
  }

  trackReviewResponded(properties: ReviewEventProperties) {
    this.track(SEGMENT_EVENTS.REVIEW_RESPONDED, properties)
  }

  trackReviewFlagged(properties: ReviewEventProperties) {
    this.track(SEGMENT_EVENTS.REVIEW_FLAGGED, properties)
  }

  trackReviewRequestSent(
    properties: ReviewEventProperties & { recipient_count: number }
  ) {
    this.track(SEGMENT_EVENTS.REVIEW_REQUEST_SENT, properties)
  }

  trackReviewAutomationCreated(
    properties: ReviewEventProperties & { automation_type: string }
  ) {
    this.track(SEGMENT_EVENTS.REVIEW_AUTOMATION_CREATED, properties)
  }

  // Loyalty tracking methods
  trackLoyaltyCardCreated(properties: LoyaltyEventProperties) {
    this.track(SEGMENT_EVENTS.LOYALTY_CARD_CREATED, properties)
  }

  trackLoyaltyCardActivated(properties: LoyaltyEventProperties) {
    this.track(SEGMENT_EVENTS.LOYALTY_CARD_ACTIVATED, properties)
  }

  trackLoyaltyPointsEarned(properties: LoyaltyEventProperties) {
    this.track(SEGMENT_EVENTS.LOYALTY_POINTS_EARNED, properties)
  }

  trackLoyaltyPointsRedeemed(properties: LoyaltyEventProperties) {
    this.track(SEGMENT_EVENTS.LOYALTY_POINTS_REDEEMED, properties)
  }

  trackLoyaltyRewardClaimed(
    properties: LoyaltyEventProperties & { reward_type: string }
  ) {
    this.track(SEGMENT_EVENTS.LOYALTY_REWARD_CLAIMED, properties)
  }

  trackLoyaltyTierChanged(
    properties: LoyaltyEventProperties & { old_tier?: string }
  ) {
    this.track(SEGMENT_EVENTS.LOYALTY_TIER_CHANGED, properties)
  }

  // Analytics tracking methods
  trackReportViewed(properties: AnalyticsEventProperties) {
    this.track(SEGMENT_EVENTS.REPORT_VIEWED, properties)
  }

  trackReportExported(
    properties: AnalyticsEventProperties & { export_format?: string }
  ) {
    this.track(SEGMENT_EVENTS.REPORT_EXPORTED, properties)
  }

  trackDashboardViewed(properties: AnalyticsEventProperties) {
    this.track(SEGMENT_EVENTS.DASHBOARD_VIEWED, properties)
  }

  trackMetricFiltered(properties: AnalyticsEventProperties) {
    this.track(SEGMENT_EVENTS.METRIC_FILTERED, properties)
  }

  trackDateRangeChanged(properties: AnalyticsEventProperties) {
    this.track(SEGMENT_EVENTS.DATE_RANGE_CHANGED, properties)
  }

  // Workflow tracking methods
  trackWorkflowCreated(properties: WorkflowEventProperties) {
    this.track(SEGMENT_EVENTS.WORKFLOW_CREATED, properties)
  }

  trackWorkflowEdited(properties: WorkflowEventProperties) {
    this.track(SEGMENT_EVENTS.WORKFLOW_EDITED, properties)
  }

  trackWorkflowDeleted(properties: WorkflowEventProperties) {
    this.track(SEGMENT_EVENTS.WORKFLOW_DELETED, properties)
  }

  trackWorkflowActivated(properties: WorkflowEventProperties) {
    this.track(SEGMENT_EVENTS.WORKFLOW_ACTIVATED, properties)
  }

  trackWorkflowDeactivated(properties: WorkflowEventProperties) {
    this.track(SEGMENT_EVENTS.WORKFLOW_DEACTIVATED, properties)
  }

  trackWorkflowTested(properties: WorkflowEventProperties) {
    this.track(SEGMENT_EVENTS.WORKFLOW_TESTED, properties)
  }

  // User Management tracking methods
  trackUserInvited(
    properties: BaseEventProperties & { invited_email: string; role: string }
  ) {
    this.track(SEGMENT_EVENTS.USER_INVITED, properties)
  }

  trackUserRemoved(
    properties: BaseEventProperties & { removed_user_id: string }
  ) {
    this.track(SEGMENT_EVENTS.USER_REMOVED, properties)
  }

  trackUserRoleChanged(
    properties: BaseEventProperties & {
      user_id: string
      old_role: string
      new_role: string
    }
  ) {
    this.track(SEGMENT_EVENTS.USER_ROLE_CHANGED, properties)
  }

  trackTeamCreated(
    properties: BaseEventProperties & { team_name: string; team_id: string }
  ) {
    this.track(SEGMENT_EVENTS.TEAM_CREATED, properties)
  }

  trackTeamUpdated(
    properties: BaseEventProperties & { team_id: string; team_name?: string }
  ) {
    this.track(SEGMENT_EVENTS.TEAM_UPDATED, properties)
  }

  // Additional Feature Usage tracking methods
  trackFeatureConfigured(properties: FeatureEventProperties) {
    this.track(SEGMENT_EVENTS.FEATURE_CONFIGURED, properties)
  }

  trackSearchPerformed(
    properties: FeatureEventProperties & {
      search_query: string
      results_count?: number
    }
  ) {
    this.track(SEGMENT_EVENTS.SEARCH_PERFORMED, properties)
  }

  trackFilterApplied(properties: FeatureEventProperties) {
    this.track(SEGMENT_EVENTS.FILTER_APPLIED, properties)
  }

  trackExportInitiated(
    properties: FeatureEventProperties & {
      export_type: string
      export_format?: string
    }
  ) {
    this.track(SEGMENT_EVENTS.EXPORT_INITIATED, properties)
  }

  // What's New tracking methods
  trackWhatsNewPopupViewed(properties: WhatsNewEventProperties) {
    this.track(SEGMENT_EVENTS.WHATS_NEW_POPUP_VIEWED, properties)
  }

  trackWhatsNewPopupDismissed(properties: WhatsNewEventProperties) {
    this.track(SEGMENT_EVENTS.WHATS_NEW_POPUP_DISMISSED, properties)
  }

  trackWhatsNewFeatureClicked(properties: WhatsNewEventProperties) {
    this.track(SEGMENT_EVENTS.WHATS_NEW_FEATURE_CLICKED, properties)
  }

  trackWhatsNewPageViewed(properties: WhatsNewEventProperties) {
    this.track(SEGMENT_EVENTS.WHATS_NEW_PAGE_VIEWED, properties)
  }

  trackWhatsNewSalesClicked(properties: WhatsNewEventProperties) {
    this.track(SEGMENT_EVENTS.WHATS_NEW_SALES_CLICKED, properties)
  }

  trackWhatsNewSeeAllClicked(properties: WhatsNewEventProperties) {
    this.track(SEGMENT_EVENTS.WHATS_NEW_SEE_ALL_CLICKED, properties)
  }

  // Additional Error tracking methods
  trackAPIError(properties: ErrorEventProperties) {
    this.track(SEGMENT_EVENTS.API_ERROR, properties)
  }

  trackValidationError(
    properties: ErrorEventProperties & { field_name?: string }
  ) {
    this.track(SEGMENT_EVENTS.VALIDATION_ERROR, properties)
  }
}

export const createSegmentTracker = (analytics: AnalyticsBrowser | null) => {
  return new SegmentTracker(analytics)
}
